import { computed, Injectable, signal } from '@angular/core';
import { QueryParamsService } from '../query-params/query-params.service';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  skip,
  Subscription,
  switchMap,
} from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import {
  CategoryTree,
  CategoryTypeWithState,
  FlattenedSubCategory,
  LanguageRegionMap,
  MainCategory,
  MidCategory,
  SearchFilter,
  SearchFilters2,
  SearchFiltersQueryParams,
  SearchFiltersState,
  SearchFiltersV1,
  SubCategory,
  TransformedSearchFilters,
} from 'src/app/types';
import { ActivatedRoute, Router } from '@angular/router';
import { QueryParamsChangeType } from '../../enums/query-param-change-type';
import { languageRegionCodesMap } from '../../constants';
import { TranslateService } from '../translation/translate.service';
import { AuthService } from '../auth/auth.service';
import { ClientService } from '../client/client.service';
import { FilterCategoryTreeState, InitialFilterCategoryTree } from 'src/app/types/filters';
import { MarketplaceService } from '../marketplace/marketplace.service';
import { AuctionsService } from '../auctions/auctions.service';


export interface ExtraParams {
   [key: string]: any;
}


@Injectable({
  providedIn: 'root',
})
export class FilterService {
  public filterKeys = signal<string[]>([]);
  public filtersLoaded = signal<boolean>(false);
  public activeFilterCount = computed<number>(() => {
    let filterCount: number = 0;

    for (const [key, value] of Object.entries(
      this.queryParamsService.qParams()
    )) {
      if (this.filterKeys().includes(key)) {
        filterCount += value.length;
      }
    }

    return filterCount;
  });

  clientTree?: CategoryTree;
  clientTreeLoaded = false;
  midCategoriesLoaded = false;
  subcategoriesLoaded = false;
  typesLoaded = false;

  selectedMainCat?: string;
  selectedMidCat?: string;
  selectedSubCat?: string;
  selectedType?: string;

  searchFilters: TransformedSearchFilters[] = [];
  originalFilters: TransformedSearchFilters[] = [];
  midCategories: MidCategory[] = [];
  subcategories: SubCategory[] = [];
  topParams: any[] = [];
  types: CategoryTypeWithState[] = [];
  queryParams: SearchFiltersQueryParams = {};
  shortName: string | null = null;

  backToForSaleParams: any = {};

  debounceTime: number = 150;
  debounceTimeVariable: any = null;

  queryParamChangeType: any;
  queryParamFilterKeyType: any;
  searchFiltersEnumNames: SearchFiltersState[] = [];

  originalFilterList: TransformedSearchFilters[] = [];
  originalFilterListSet: boolean = false;
  filterOptionsSubscription!: Subscription;
  constructor(
    private queryParamsService: QueryParamsService,
    private activatedRoute: ActivatedRoute,
    private clientService: ClientService,
    private translateService: TranslateService,
    private authService: AuthService,
    private router: Router,
    private marketplaceService: MarketplaceService,
    private auctionService: AuctionsService,
  ) {

    if(this.activatedRoute.snapshot.params['shortName']) {
      this.shortName = this.activatedRoute.snapshot.params['shortName'];
    }
    
    this.listenToQueryParamsChangeType();
    this.listenToQueryParamsFilterKeyType();
    // this.listenToBreakpointObserver();
    this.listenToMaincategoryQueryParamChange();
    this.listenToMidcategoryQueryParamChange();
    this.listenToSubcategoryQueryParamChange();
    this.listenToTypeQueryParamChange();
  }


  public getInitialFilterOptions(extraParams?:ExtraParams) {
    // TODO: Investigate switch over to resource API after v19 upgrade
    this.filterOptionsSubscription?.unsubscribe();
    this.filterOptionsSubscription = this.activatedRoute.queryParams
      .pipe(
        debounceTime(this.debounceTime),
        switchMap((params: any) => {
          const defaultParams = this.queryParamsService.extractDefaultParams();
          params = {...params, ...defaultParams};
          this.queryParams = params;
          this.topParams = [];

          // Category flags and loading
          if (this.clientTreeLoaded) {
            this.midCategoriesLoaded = !!params['mainCategory'];
            this.subcategoriesLoaded = !!params['midCategory'];
            this.typesLoaded = !!params['subcategory'];

            if (params['mainCategory']) {
              if(Array.isArray(params['mainCategory'])) {
                this.selectedMainCat = this.queryParamsService.qParams()['mainCategory'][0];
              } else {
                this.selectedMainCat = this.queryParamsService.qParams()['mainCategory'];
              }
              
              this.clientTree?.mainCategories.forEach(
                (mainCat: CategoryTypeWithState) => (mainCat['isActive'] = mainCat.name === this.selectedMainCat ? true : false)
              );
              this.loadMidCat();
            }
            if (params['midCategory']) this.loadSubCat();
            if (params['subcategory']) this.loadType();
            if (params['type']) this.setType();
          }

          // Determine which API to call
          const type =
            this.queryParamChangeType === QueryParamsChangeType.MAC_FOLDERS ||
            this.queryParamChangeType === QueryParamsChangeType.FOR_SALE ||
            this.queryParamChangeType === QueryParamsChangeType.PRIVATE_PORTAL ||
            this.queryParamChangeType === QueryParamsChangeType.AUCTION
              ? 'base'
              : 'new';
          const lang = this.translateService.language;
          const _lang = lang.toUpperCase();
          let regionCode = this.clientService.getRegionCode();

          const token = this.authService?.getAccessToken();
          const headers = {
            'Accept-Language': regionCode,
            'Content-Type': 'application/json;',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          };

          let _searchFilterUrl = this.clientService.getClientSearchFiltersV2Url(_lang, params);
          let _newSearchFilterUrl = this.clientService.getNewClientSearchFiltersV2Url(
            _lang,
            params
          );

          if (extraParams) {
            params = structuredClone(params);
            Object.keys(extraParams).forEach((key) => {
              params[key] = extraParams[key];
            });
          }
          
          if(this.queryParamChangeType === QueryParamsChangeType.PRIVATE_PORTAL && this.shortName !== null) {
            _searchFilterUrl = this.marketplaceService.getPrivateSearchFiltersUrl(this.shortName, _lang, params);
            _newSearchFilterUrl = this.marketplaceService.getNewPrivateSearchFiltersUrl(this.shortName, _lang, params);
          }

          if(this.queryParamChangeType === QueryParamsChangeType.AUCTION && this.shortName !== null) {
            _searchFilterUrl = this.auctionService.getSearchFiltersUrl(undefined, this.shortName, _lang, params);
            _newSearchFilterUrl = this.auctionService.getNewSearchFiltersUrl(undefined, this.shortName, _lang, params);
          } 

          const url =
            type === 'base'
              ? _searchFilterUrl
              : _newSearchFilterUrl;

          return fromFetch(url, {
            method: 'GET',
            headers,
          }).pipe(
            switchMap((response) => {
              if (response.ok) {
                return response.json();
              } else {
                throw new Error(`HTTP ${response.status}`);
              }
            }),
            map((data: SearchFilter[]) => ({ data, params, type })),
            catchError((error) => {
              console.error('Search filter fetch failed:', error);
              this.filtersLoaded.set(true);
              return of(null);
            })
          );
        })
      )
      .subscribe((result) => {
        if (!result) return;

        const { data, params, type } = result;

        if (type === 'base') {
          let transformedData: TransformedSearchFilters[] = [];

          this.searchFiltersEnumNames = [];
          transformedData = data.map((f: any) => ({
            name: f.name,
            enumName: f.searchKey,
            type: f.type.toLocaleLowerCase() as SearchFiltersV1['type'],
            unitTranslationKey: f.unit,
            units: f.units,
            values: f.values
              ? f.values.map((v: any) => ({
                  count: v.count,
                  isChecked: v.isChecked,
                  translationKey: v.searchValue,
                  label: v.value,
                }))
              : [],
          }));

          // Only set the original filter list once
          if (!this.originalFilterListSet) {
            this.originalFilterList = [...transformedData];
            this.originalFilterListSet = true;
          }

          this.searchFilters = transformedData;
          this.originalFilters = transformedData;
          const filterKeys: string[] = this.searchFilters.map(
            (f) => f.enumName
          );
          this.filterKeys.set(['mainCategory', 'type', ...filterKeys]);
          this.filtersLoaded.set(true);

          this.searchFiltersEnumNames = this.searchFilters.map((item) => ({
            enumName: item.enumName,
            existed: false,
          }));

          this.setActiveFiltersChips(params);
        } else {
          // 'new' type logic: apply counts from result
          const responseData = data as unknown as SearchFilters2[];

          for (let i = 0; i < this.searchFilters.length; i++) {
            const filter = this.searchFilters[i];
            if (
              filter.enumName !== this.queryParamFilterKeyType ||
              ((params[this.queryParamFilterKeyType] ||
                !params[this.queryParamFilterKeyType]) &&
                params[this.queryParamFilterKeyType] &&
                params[this.queryParamFilterKeyType].length === 0)
            ) {
              if (filter.values) {
                for (let j = 0; j < filter.values.length; j++) {
                  filter.values[j].count = 0;
                }
              }
            }
          }

          for (const original of this.searchFilters) {
            for (const filterUpdate of responseData) {
              for (const newVal of filterUpdate.values) {
                if (
                  newVal.enumName !== this.queryParamFilterKeyType ||
                  ((params[this.queryParamFilterKeyType] ||
                    !params[this.queryParamFilterKeyType]) &&
                    params[this.queryParamFilterKeyType] &&
                    params[this.queryParamFilterKeyType].length === 0)
                ) {
                  if (original.enumName === newVal.enumName) {
                    for (const val of original.values || []) {
                      if (
                        val.translationKey === newVal.valueTranslationKey ||
                        val.translationKey === newVal.value
                      ) {
                        val.count += 1;
                      }
                    }
                  }
                }
              }
            }
          }

          this.setActiveFiltersChips(params);
          this.filtersLoaded.set(true);
        }
      });
  }

  private getClientSearchFilters(params: any, type: 'new' | 'base'): void {
    clearTimeout(this.debounceTimeVariable);

    for (let i = 0; i < this.searchFiltersEnumNames.length; i++) {
      let object = this.searchFiltersEnumNames[i];
      for (let obj in params) {
        if (object.enumName === obj) {
          if (
            (params[object.enumName] && params[object.enumName].length === 0) ||
            !params[object.enumName]
          ) {
            object.existed = false;
          } else {
            object.existed = true;
          }
        }
      }
    }

    if (!this.searchFiltersEnumNames.find((item) => item.existed)) {
      type = 'base';
    }

    // Set up language and authorization token
    const lang = this.translateService.language;
    const _lang = lang.toUpperCase();
    let regionCode = this.clientService.getRegionCode();
    const token = this.authService?.getAccessToken();
    // Request headers
    const headers = {
      'Accept-Language': regionCode,
      'Content-Type': 'application/json;',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    if (type === 'base') {
      this.debounceTimeVariable = setTimeout(async () => {
        try {
          // Make the API request using the new Fetch-based method
          const data: SearchFilter[] =
            await this.clientService.getClientSearchFiltersV2(_lang, params, {
              headers,
            });

          let transformedData: TransformedSearchFilters[] = [];

          this.searchFiltersEnumNames = [];
          transformedData = data.map((f) => ({
            name: f.name,
            enumName: f.searchKey,
            type: f.type.toLocaleLowerCase() as SearchFiltersV1['type'],
            unitTranslationKey: f.unit,
            units: f.units,
            values: f.values
              ? f.values.map((v) => ({
                  count: v.count,
                  isChecked: v.isChecked,
                  translationKey: v.searchValue,
                  label: v.value,
                }))
              : [],
          }));

          this.searchFilters = transformedData;
          this.originalFilters = transformedData;
          const filterKeys: string[] = this.searchFilters.map(
            (_data) => _data.enumName
          );
          this.filterKeys.set(['mainCategory', 'type', ...filterKeys]);
          this.filtersLoaded.set(true);

          this.searchFiltersEnumNames = this.searchFilters.map((item) => {
            return { enumName: item.enumName, existed: false };
          });

          this.setActiveFiltersChips(params);
        } catch (error) {
          this.filtersLoaded.set(true);
          // TODO handler error
        }
      }, this.debounceTime);
    } else {
      this.debounceTimeVariable = setTimeout(async () => {
        try {
          // Make the API request using the new Fetch-based method
          const data: SearchFilters2[] =
            await this.clientService.getNewClientSearchFiltersV2(
              _lang,
              params,
              {
                headers,
              }
            );

          for (let i = 0; i < this.searchFilters.length; i++) {
            let originalObject = this.searchFilters[i];
            if (
              originalObject.enumName !== this.queryParamFilterKeyType ||
              ((params[this.queryParamFilterKeyType] ||
                !params[this.queryParamFilterKeyType]) &&
                params[this.queryParamFilterKeyType] &&
                params[this.queryParamFilterKeyType].length === 0)
            ) {
              if (originalObject.values !== null) {
                for (let j = 0; j < originalObject.values.length; j++) {
                  originalObject.values[j].count = 0;
                }
              }
            }
          }

          for (let i = 0; i < this.searchFilters.length; i++) {
            let originalObject = this.searchFilters[i];
            for (let j = 0; j < data.length; j++) {
              let parameterObject = data[j].values;
              for (let k = 0; k < parameterObject.length; k++) {
                let parameterObjectValue = parameterObject[k];
                if (
                  parameterObjectValue.enumName !==
                    this.queryParamFilterKeyType ||
                  ((params[this.queryParamFilterKeyType] ||
                    !params[this.queryParamFilterKeyType]) &&
                    params[this.queryParamFilterKeyType] &&
                    params[this.queryParamFilterKeyType].length === 0)
                ) {
                  if (originalObject.values !== null) {
                    for (let l = 0; l < originalObject.values.length; l++) {
                      let actualParamter = originalObject.values[l];
                      if (
                        parameterObjectValue.enumName ===
                        originalObject.enumName
                      ) {
                        if (
                          actualParamter.translationKey ===
                            parameterObjectValue.valueTranslationKey ||
                          actualParamter.translationKey ===
                            parameterObjectValue.value
                        ) {
                          actualParamter.count = actualParamter.count + 1;
                        }
                      }
                    }
                  }
                }
              }
            }
          }

          this.setActiveFiltersChips(params);

          this.filtersLoaded.set(true);
        } catch (error) {
          this.filtersLoaded.set(true);
          // TODO handler error
        }
      }, this.debounceTime);
    }
  }

  public flattenSubCategories(tree: CategoryTree): FlattenedSubCategory[] {
    const result: FlattenedSubCategory[] = [];

    for (const mainCategory of tree.mainCategories) {
      if (!mainCategory.midCategories) continue;

      for (const midCategory of mainCategory.midCategories) {
        if (!midCategory.subCategories) continue;

        for (const subCategory of midCategory.subCategories) {
          result.push({
            subCategoryName: subCategory.name,
            subCategoryTranslationKey: subCategory.translationKey,
            midCategoryName: midCategory.name,
            mainCategoryName: mainCategory.name,
          });
        }
      }
    }

    return result;
  }

  private setActiveFiltersChips(params: SearchFiltersQueryParams): void {
    const top = [];
    for (let item in params) {
      if (item !== 'pageIndex' && item !== 'pageSize') {
        if (!Array.isArray(params[item])) {
          if (
            item === 'mainCategory' ||
            // item === 'midCategory' ||
            // item === 'subcategory' ||
            item === 'type'
          ) {
            top.push(
              this.getTranslationValueWithTranslation(item, params[item])
            );
          } else {
            const foundItem = this.searchFilters.find(
              (obj) => obj.enumName === item
            );

            if (foundItem) {
              const matchedValue = foundItem.values?.find(
                (v) => v.translationKey === (params[item] as unknown as string)
              );

              top.push({
                name: item,
                value: params[item],
                translationValue: matchedValue?.label,
              });
            }
          }
        } else {
          for (let obj of params[item]) {
            if (
              item === 'mainCategory' ||
              // item === 'midCategory' ||
              // item === 'subcategory' ||
              item === 'type'
            ) {
              top.push(this.getTranslationValueWithTranslation(item, obj));
            } else {
              const foundItem = this.searchFilters.find(
                (obj) => obj.enumName === item
              );
              if (foundItem) {
                const matchedValue = foundItem.values?.find(
                  (v) => v.translationKey === obj
                );

                let object = {
                  name: item,
                  value: obj,
                  translationValue: matchedValue?.label,
                };
                top.push(object);
              }
            }
          }
        }
      }
    }
    this.topParams = top;
  }

  private loadMidCat(): void {
    const foundMainCat = this.returnMainCategory();
    if (foundMainCat && foundMainCat.midCategories) {
      this.midCategories = foundMainCat.midCategories;
      this.selectedMidCat = this.queryParamsService.qParams()['midCategory'];
    }
    this.midCategoriesLoaded = true;
  }

  private loadSubCat(): void {
    const foundMainCat = this.returnMainCategory();
    const foundMidCat = this.returnMidCategory(foundMainCat);
    this.midCategories.forEach(
      (midCat: CategoryTypeWithState) => (midCat['isActive'] = midCat.name === this.selectedMidCat ? true : false)
    );
    if (foundMidCat?.subCategories) {
      this.subcategories = foundMidCat.subCategories;
      this.selectedSubCat = this.queryParamsService.qParams()['subcategory']
    }
    this.subcategoriesLoaded = true;
  }

  private loadType(): void {
    const foundMainCat = this.returnMainCategory();
    const foundMidCat = this.returnMidCategory(foundMainCat);
    const foundSubCat = this.returnSubCategory(foundMidCat);
    this.subcategories.forEach(
      (subCat: CategoryTypeWithState) => (subCat['isActive'] = subCat.name === this.selectedSubCat ? true : false)
    );
    if (foundSubCat?.types) {
      this.types = foundSubCat.types;
      this.selectedType = this.queryParamsService.qParams()['type']
    }
    this.typesLoaded = true;
  }

  private setType(): void {
    const foundMainCat = this.returnMainCategory();
    const foundMidCat = this.returnMidCategory(foundMainCat);
    const foundSubCat = this.returnSubCategory(foundMidCat);
    this.subcategories.forEach(
      (subCat: CategoryTypeWithState) => (subCat['isActive'] = subCat.name === this.selectedSubCat ? true : false)
    );
    if (foundSubCat?.types) {
      this.types = foundSubCat.types;
      this.selectedType = this.queryParamsService.qParams()['type'];
      this.types.forEach(
        (type: CategoryTypeWithState) => (type['isActive'] = type.name === this.selectedType ? true : false)
      );
    }
    this.typesLoaded = true;
  }

  private returnMainCategory(): MainCategory | undefined {
    return this.clientTree?.mainCategories.find(
      (mainCat) =>
        mainCat.name ===
      this.queryParamsService.qParams()['mainCategory']

    );
  }

  private returnMidCategory(mainCat?: MainCategory): MidCategory | undefined {
    if (!mainCat || !mainCat.midCategories) {
      return undefined;
    }
    return mainCat.midCategories.find(
      (midCat) =>
        midCat.name === this.queryParamsService.qParams()['midCategory']

    );
  }

  private returnSubCategory(midCat?: MidCategory): SubCategory | undefined {
    if (!midCat || !midCat.subCategories) {
      return undefined;
    }

    return midCat.subCategories.find(
      (subCat) =>
        subCat.name === this.queryParamsService.qParams()['subcategory']

    );
  }

  private getTranslationValueWithTranslation(
    item: string,
    value: string | string[]
  ) {
    let mainCat = null;
    let midCat = null;
    if (!this.clientTree) {
      return;
    }
    if (item === 'mainCategory') {
      for (let i = 0; i < this.clientTree.mainCategories.length; i++) {
        let mainCatItem = this.clientTree.mainCategories[i];
        if (mainCatItem.name === value) {
          mainCat = mainCatItem;
          return {
            type: item,
            name: mainCat.name,
            value: mainCat.translationKey,
            translationValue: mainCat.translationValue,
            totalRecords: mainCat.count,
            midCategories: this.midCategories,
          };
        }
      }
    }
    if (item === 'midCategory') {
      for (let i = 0; i < this.clientTree.mainCategories.length; i++) {
        let mainCatItem = this.clientTree.mainCategories[i];
        for (let j = 0; j < (mainCatItem.midCategories ?? []).length; j++) {
          if (mainCatItem.midCategories) {
            let midCatItem = mainCatItem.midCategories[j];
            if (midCatItem.name === value) {
              return {
                type: item,
                name: midCatItem.name,
                value: midCatItem.translationKey,
                translationValue: midCatItem.translationValue,
                totalRecords: this.midCategories.length,
                subcategories: this.subcategories,
              };
            }
          }
        }
      }
    }
    if (item === 'subcategory') {
      for (let i = 0; i < this.clientTree.mainCategories.length; i++) {
        let mainCatItem = this.clientTree.mainCategories[i];
        if (mainCatItem.midCategories) {
          for (let j = 0; j < mainCatItem.midCategories.length; j++) {
            let midCatItem = mainCatItem.midCategories[j];
            if (midCatItem.subCategories) {
              for (let k = 0; k < midCatItem.subCategories.length; k++) {
                let subCatItem = midCatItem.subCategories[k];
                if (subCatItem.name === value) {
                  return {
                    type: item,
                    name: subCatItem.name,
                    value: subCatItem.translationKey,
                    translationValue: subCatItem.translationValue,
                    totalRecords: this.subcategories.length,
                    types: this.types,
                  };
                }
              }
            }
          }
        }
      }
    }
    if (item === 'type') {
      for (let i = 0; i < this.clientTree.mainCategories.length; i++) {
        let mainCatItem = this.clientTree.mainCategories[i];
        if (mainCatItem.midCategories) {
          for (let j = 0; j < mainCatItem.midCategories.length; j++) {
            let midCatItem = mainCatItem.midCategories[j];
            if (midCatItem.subCategories) {
              for (let k = 0; k < midCatItem.subCategories.length; k++) {
                let subCatItem = midCatItem.subCategories[k];
                if (subCatItem.types) {
                  for (let l = 0; l < subCatItem.types.length; l++) {
                    let typeItem = subCatItem.types[l];
                    if (typeItem.name === value) {
                      return {
                        type: item,
                        name: typeItem.name,
                        value: typeItem.translationKey,
                        translationValue: typeItem.translationValue,
                        totalRecords: this.types.length,
                      };
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    return;
  }

  private listenToQueryParamsChangeType(): void {
    this.queryParamsService.currentSharedQueryParamsChangeType.subscribe(
      (type: any) => {
        this.queryParamChangeType = type;
      }
    );
  }

  private listenToQueryParamsFilterKeyType(): void {
    this.queryParamsService.currentSharedQueryParamsFilterKeyType.subscribe(
      (type: any) => {
        this.queryParamFilterKeyType = type;
      }
    );
  }

  private listenToMaincategoryQueryParamChange(): void {
    this.activatedRoute.queryParamMap
      .pipe(
        map((params) => params.get('mainCategory')),
        distinctUntilChanged(),
        skip(1)
      )
      .subscribe((qp) => {
        this.clientTree?.mainCategories.forEach(
          (mainCat: CategoryTypeWithState) => (mainCat['isActive'] = mainCat.name === this.selectedMainCat ? true : false)
        );
      });
  }

  private listenToMidcategoryQueryParamChange(): void {
    this.activatedRoute.queryParamMap
      .pipe(
        map((params) => params.get('midCategory')),
        distinctUntilChanged(),
        skip(1)
      )
      .subscribe((qp) => {
        this.midCategories.forEach(
          (midCat: CategoryTypeWithState) => (midCat['isActive'] = midCat.name === qp ? true : false)
        );
      });
  }

  private listenToSubcategoryQueryParamChange(): void {
    this.activatedRoute.queryParamMap
      .pipe(
        map((params) => params.get('subcategory')),
        distinctUntilChanged(),
        skip(1)
      )
      .subscribe((qp) => {
        this.subcategories.forEach(
          (subCat: CategoryTypeWithState) => (subCat['isActive'] = subCat.name === qp ? true : false)
        );
      });
  }

  private listenToTypeQueryParamChange(): void {
    this.activatedRoute.queryParamMap
      .pipe(
        map((params) => params.get('type')),
        distinctUntilChanged(),
        skip(1)
      )
      .subscribe((qp) => {
        this.types.forEach(
          (type: CategoryTypeWithState) => (type['isActive'] = type.name === qp ? true : false)
        );
      });
  }

  public reset(shortname?: string | null, force = false) {
    this.shortName = shortname ? shortname : null;
    this.clientTree = undefined;
    this.clientTreeLoaded = false;
  }

  public clearFilters(navigate = true): void {
    // Reset signals
    this.filtersLoaded.set(false);
    this.filterKeys.set([]);

    // Clear internal state
    this.searchFilters = this.originalFilters;
    this.searchFiltersEnumNames = [];

    /* Clearing these makes icon-row flicker and act weird. Temp removal
    this.topParams = [];

    this.midCategories = [];
    this.subcategories = [];
    this.types = [];

    this.midCategoriesLoaded = false;
    this.subcategoriesLoaded = false;
    this.typesLoaded = false;
    */

    this.selectedMainCat = undefined;
    this.selectedMidCat = undefined;
    this.selectedSubCat = undefined;
    this.selectedType = undefined;

    this.backToForSaleParams = {};

    // Optionally reset query parameters (except pagination)
    // const preservedParams: any = {};
    // if (this.queryParams['pageIndex']) {
    //   preservedParams['pageIndex'] = this.queryParams['pageIndex'];
    // }
    // if (this.queryParams['pageSize']) {
    //   preservedParams['pageSize'] = this.queryParams['pageSize'];
    // }

    // Remove all other filter-related query params
    // this.router.navigate([], {
    //   queryParams: preservedParams,
    // });

    if (navigate) {
      this.router.navigate([]);
    }
  }
}
